/**
 * DayOpenScreen — gate that fronts the billing surface until a billing
 * session has been opened for today.
 *
 * Layout: centred card with opening-cash entry. The same component handles
 * the "session is loading" and "no permission" states so callers don't have
 * to coordinate them.
 *
 * Keyboard:
 *   0-9 .          build amount
 *   Backspace      delete last digit
 *   Enter          open day (if amount valid)
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useState } from 'react';
import { Sun, Lock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAppStore } from '@/store/useAppStore';
import { useBillingSession } from '@/billing/useBillingSession';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

interface Props {
  onOpened?: () => void;
}

export function DayOpenScreen({ onOpened }: Props) {
  const currentUser = useAppStore((s) => s.currentUser);
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const { openSession, isLoading } = useBillingSession();

  const [amountInput, setAmountInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [busy, setBusy] = useState(false);

  function pushKey(k: string) {
    setAmountInput((prev) => {
      if (k === '.') {
        if (prev.includes('.')) return prev;
        return prev === '' ? '0.' : `${prev}.`;
      }
      const dot = prev.indexOf('.');
      if (dot !== -1 && prev.length - dot > 2) return prev;
      if (prev === '0' && k !== '.') return k;
      return `${prev}${k}`;
    });
  }

  function backspace() {
    setAmountInput((prev) => prev.slice(0, -1));
  }

  async function handleOpen() {
    const amt = parseFloat(amountInput || '0');
    if (!Number.isFinite(amt) || amt < 0) {
      setError('Enter a valid opening cash amount');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await openSession(amt);
      notifySuccess(`Day opened with ${formatCurrency(amt)} in the drawer.`);
      onOpened?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the day');
    } finally {
      setBusy(false);
    }
  }

  // Keyboard handlers
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
      if (inField) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!busy) handleOpen();
        return;
      }
      if (e.key === 'Backspace') { e.preventDefault(); backspace(); return; }
      if (e.key >= '0' && e.key <= '9') { e.preventDefault(); pushKey(e.key); return; }
      if (e.key === '.') { e.preventDefault(); pushKey('.'); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, amountInput]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-body text-fg-tertiary">
        Loading session…
      </div>
    );
  }

  if (!activeStoreId || !currentUser) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <div className="rounded-lg border border-stroke-hairline/15 bg-bg-surface px-6 py-8">
          <Lock className="mx-auto mb-3 text-fg-tertiary" />
          <div className="text-heading text-fg-primary">Sign in to start the day</div>
          <div className="mt-1 text-body text-fg-secondary">
            A user and active store are required before opening a billing session.
          </div>
        </div>
      </div>
    );
  }

  const tenderedNum = parseFloat(amountInput) || 0;
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-md rounded-lg border border-stroke-hairline/15 bg-bg-surface p-6 shadow-e1">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent/10 text-accent-ink">
            <Sun size={20} />
          </div>
          <div>
            <div className="text-heading text-fg-primary">Open the day</div>
            <div className="text-label uppercase text-fg-tertiary">{today}</div>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-label uppercase text-fg-tertiary">Opening cash in drawer</label>
          <div className={cn(
            'mt-1 rounded-md border px-4 py-3 text-right text-display tabular-nums',
            tenderedNum > 0
              ? 'border-accent/40 text-fg-primary'
              : 'border-stroke-hairline/15 text-fg-tertiary',
          )}>
            {amountInput === '' ? formatCurrency(0) : `₹${amountInput}`}
          </div>

          {/* Keypad */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            {(['7','8','9','4','5','6','1','2','3','.','0'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => pushKey(k)}
                className="flex h-12 items-center justify-center rounded-md border border-stroke-hairline/15 bg-bg-canvas text-[20px] font-medium text-fg-primary tabular-nums hover:border-accent/40 hover:bg-bg-subtle transition-colors"
              >
                {k}
              </button>
            ))}
            <button
              type="button"
              onClick={backspace}
              aria-label="Backspace"
              className="flex h-12 items-center justify-center rounded-md border border-stroke-hairline/15 bg-bg-canvas text-fg-secondary hover:border-accent/40 hover:bg-bg-subtle transition-colors"
            >
              ⌫
            </button>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => setAmountInput('')}
            disabled={busy || amountInput === ''}
          >
            Clear
          </Button>
          <Button onClick={handleOpen} disabled={busy}>
            {busy ? 'Opening…' : 'Open day  ·  Enter'}
          </Button>
        </div>

        <div className="mt-4 text-label uppercase text-fg-tertiary text-center">
          You can enter ₹0 if the drawer is empty.
        </div>
      </div>
    </div>
  );
}
